import _ from "lodash";
// External
import React from "react";
import withDerivedProps from "lib/withDerivedProps";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import classNames from "classnames";
import { AI_GRADING_HELP_CENTER_URL } from "Constants";

// Design system
import {
  ToggleSwitch,
  Button,
  Modal,
  Tag,
  IconButton,
  Tooltip,
  Avatar,
} from "@toddle-edu/ds-web";
import {
  ChevronDownOutlined,
  ChevronUpOutlined,
  InformationOutlined,
  PageSquareOutlined,
  TickBadgeOutlined,
  LinkOutlined,
} from "@toddle-edu/ds-icons";

// Modules
import { openLinkInNewTab } from "modules/Services";
import { updateSettings } from "modules/ConfigurationThunks";
import { setAIEvaluationExpandedState } from "Assessments/modules/AssessmentModule/AssessmentActions";

// Components
import UIBaseComponent from "UIComponents/UIBaseComponent";

// Utils
import { AI_TRANSLATION_LANGUAGES } from "@toddle-edu/common-utils";
import { getAssignmentHasTeacherEvaluation } from "ClassRoom/modules/utils";
import { getSettingValue } from "Utils/ConfigurationUtils";

// Sccs
import classes from "./FieldAIEvaluation.scss";

const AI_EVALUATION_PRACTICES = [
  {
    key: "clear_instructions",
    icon: PageSquareOutlined,
    color: "blue",
    titleKey: "classRoom:ai_evaluation_practice_clear_instructions_title",
    descriptionKey:
      "classRoom:ai_evaluation_practice_clear_instructions_description",
  },
  {
    key: "objective_tools",
    icon: TickBadgeOutlined,
    color: "pink",
    titleKey: "classRoom:ai_evaluation_practice_objective_tools_title",
    descriptionKey:
      "classRoom:ai_evaluation_practice_objective_tools_description",
  },
  {
    key: "file_sharing",
    icon: LinkOutlined,
    color: "yellow",
    titleKey: "classRoom:ai_evaluation_practice_file_sharing_title",
    descriptionKey: "classRoom:ai_evaluation_practice_file_sharing_description",
  },
];

const styles = {
  containerStyle: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "auto 1fr",
  },
  labelContainerStyle: {
    width: "fit-content",
    marginBottom: "0px",
  },
};

const getAITranslationDropdownOptions = () => {
  return _.map(AI_TRANSLATION_LANGUAGES, language => {
    const { id, label, code } = language;
    return {
      key: _.toString(id),
      label,
      lng: code,
      value: code,
    };
  });
};

class FieldAIEvaluation extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      isDisableAIEvaluationWarningModalOpen: false,
    };
  }

  onCloseDialogClick = () => {
    this.setState({
      isDisableAIEvaluationWarningModalOpen: false,
    });
  };

  onDisableAIEvaluation = () => {
    const { updateInputField, fieldKey } = this.props;

    updateInputField(
      {
        [fieldKey]: false,
      },
      undefined,
      true,
      true,
      false
    );

    updateInputField(
      {
        aiEvaluationLanguage: "",
      },
      undefined,
      true,
      true,
      false
    );
  };

  onConfirmDialogClick = () => {
    this.onDisableAIEvaluation();
    this.onCloseDialogClick();
  };

  renderAIGradingDisableWarning = t => {
    const { isDisableAIEvaluationWarningModalOpen } = this.state;

    return (
      <Modal
        width="480px"
        height="fit-content"
        isOpen={isDisableAIEvaluationWarningModalOpen}
        onClose={this.onCloseDialogClick}
        dsVersion="2.0"
      >
        <Modal.Header
          title={t("ai:disable_ai_evaluation")}
          subtitle={t("ai:disable_ai_evaluation_subtext")}
          isClosable={true}
        />

        <Modal.Body>{t("ai:disable_ai_evaluation_warning_msg")}</Modal.Body>

        <Modal.Footer>
          <div className={classes.footerContainer}>
            <Button
              dsVersion="2.0"
              size="medium"
              variant="neutral"
              type="outlined"
              onClick={this.onCloseDialogClick}
            >
              {t("common:go_back")}
            </Button>
            <Button
              dsVersion="2.0"
              size="medium"
              variant="destructive"
              onClick={this.onConfirmDialogClick}
            >
              {t("common:disable")}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  };

  renderAIEvaluationSubFieldsVisiblityToggle = () => {
    const { isAIEvaluationExpanded, setAIEvaluationExpandedState } = this.props;

    const toggleAIEvaluationExpandedState = () => {
      setAIEvaluationExpandedState(!isAIEvaluationExpanded);
    };

    return (
      <div className={classes.iconWithSeparator}>
        <IconButton
          dsVersion="2.0"
          icon={
            isAIEvaluationExpanded ? (
              <ChevronUpOutlined variant="subtle" />
            ) : (
              <ChevronDownOutlined variant="subtle" />
            )
          }
          variant="neutral"
          size="small"
          type="plain"
          onClick={toggleAIEvaluationExpandedState}
        />
      </div>
    );
  };

  getAIEvaluationLanguage = () => {
    const { value, i18n, defaultLanguagePreferenceId } = this.props;

    const platformLanguage = _.get(i18n, "language", "en");
    const aiTranslationLanguages = getAITranslationDropdownOptions();

    return (
      _.find(aiTranslationLanguages, language => {
        // if user has selected a language, use that
        if (!_.isEmpty(defaultLanguagePreferenceId)) {
          return _.get(language, "key") === defaultLanguagePreferenceId;
        } else if (!_.isEmpty(value)) {
          return _.get(language, "key") === value;
        }

        if (platformLanguage === "en-US") {
          return _.get(language, "key") === "9";
        } else if (platformLanguage === "en-GB") {
          return _.get(language, "key") === "10";
        }

        // else use the platform language
        return _.get(language, "lng") === platformLanguage;
      }) || {}
    );
  };

  componentDidUpdate = prevProps => {
    const { assignmentHasTeacherEvaluation, value, mode } = this.props;

    if (
      mode !== "view" &&
      assignmentHasTeacherEvaluation !==
        prevProps.assignmentHasTeacherEvaluation
    ) {
      if (!assignmentHasTeacherEvaluation && value) {
        this.onDisableAIEvaluation();
      }
    }
  };

  onToggleSwitch = e => {
    const {
      updateInputField,
      fieldKey,
      assessmentMode,
      setAIEvaluationExpandedState,
    } = this.props;

    const isChecked = e.target.checked;

    setAIEvaluationExpandedState(isChecked);

    if (!isChecked && assessmentMode === "edit") {
      this.setState({
        isDisableAIEvaluationWarningModalOpen: true,
      });
      return;
    }

    updateInputField(
      {
        [fieldKey]: isChecked,
      },
      undefined,
      true,
      true,
      false
    );

    const { key: selectedLanguageId } = this.getAIEvaluationLanguage();
    updateInputField(
      {
        aiEvaluationLanguage: isChecked ? selectedLanguageId : "",
      },
      undefined,
      true,
      true,
      false
    );
  };

  // value will always be null as this component is just to show label and toggle switch
  shouldShowEmpty = () => false;

  renderView = () => {
    const { value } = this.props;

    return (
      <div className={classes.container}>
        <div className={classes.rightContainer}>
          <ToggleSwitch
            dsVersion="2.0"
            checked={value}
            disabled={true}
            size="medium"
          />
        </div>
      </div>
    );
  };

  renderChildren = () => {
    const {
      value,
      t,
      isAIEvaluationExpanded,
      mode,
      openLinkInNewTab,
    } = this.props;

    const handleLearnBestPractices = () => {
      openLinkInNewTab({ url: AI_GRADING_HELP_CENTER_URL });
    };

    return value && isAIEvaluationExpanded && mode === "edit" ? (
      <div className={classes.aiEvaluationCard}>
        <div className={classes.headerSection}>
          <div className="text-label-s">
            {t("classRoom:ai_evaluation_best_practices_header")}
          </div>

          <Button
            onClick={handleLearnBestPractices}
            variant="neutral"
            type="outlined"
            size="x-small"
            dsVersion="2.0"
          >
            {t("common:learn_more")}
          </Button>
        </div>
        <div className={classes.practicesList}>
          {AI_EVALUATION_PRACTICES.map(practice => {
            const IconComponent = practice.icon;
            return (
              <div key={practice.key} className={classes.practiceItem}>
                <Avatar
                  size="small"
                  dsVersion="2.0"
                  color={practice.color}
                  icon={<IconComponent color={practice.color} />}
                  shape="square"
                  iconType="icon"
                />
                <div className={classes.practiceContent}>
                  <div className="text-body-s">{t(practice.titleKey)}</div>
                  <div
                    className={classNames(
                      classes.practiceDescription,
                      "text-body-s"
                    )}
                  >
                    {t(practice.descriptionKey)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    ) : null;
  };

  renderEdit = () => {
    const { value, t, assignmentHasTeacherEvaluation } = this.props;
    const { isDisableAIEvaluationWarningModalOpen } = this.state;

    return (
      <div className={classes.container}>
        <div className={classes.leftContainer}>
          <Tooltip
            dsVersion="2.0"
            showArrow={true}
            tooltip={t("ai:ai_evaluation_information_message")}
            overlayInnerStyle={{ maxWidth: "258px" }}
          >
            <InformationOutlined size="xx-small" variant="subtle" />
          </Tooltip>
          <Tag dsVersion="2.0" size="small" color="yellow">
            {t("common:beta")}
          </Tag>
        </div>

        <div className={classes.rightContainer}>
          {value ? this.renderAIEvaluationSubFieldsVisiblityToggle() : null}
          <Tooltip
            dsVersion="2.0"
            showArrow={true}
            tooltip={t("ai:ai_evaluation_toggle_disabled_text")}
            visible={!assignmentHasTeacherEvaluation}
          >
            <ToggleSwitch
              dsVersion="2.0"
              checked={value}
              onChange={this.onToggleSwitch}
              size="medium"
              disabled={!assignmentHasTeacherEvaluation}
            />
          </Tooltip>
        </div>
        {isDisableAIEvaluationWarningModalOpen
          ? this.renderAIGradingDisableWarning(t)
          : null}
      </div>
    );
  };
}

const getDerivedProps = () => {
  const defaultLanguagePreferenceId = getSettingValue({
    name: "ConfigureAIGradingVoiceLanguage",
    readFromUserAndOrgLevel: true,
  });

  return {
    defaultLanguagePreferenceId,
    containerStyle: styles.containerStyle,
    labelContainerStyle: styles.labelContainerStyle,
  };
};

const mapStateToProps = (state, ownProps) => {
  const { isLearningGoalsEvaluationEnabled, assessmentTools } = ownProps;

  const assessmentToolsEdges = _.get(assessmentTools, "edges", []);
  const assignmentHasTeacherEvaluation = getAssignmentHasTeacherEvaluation({
    isLearningGoalsEvaluationEnabled,
    assessmentTools: assessmentToolsEdges,
  });

  return {
    isAIEvaluationExpanded: _.get(state, "assessment.isAIEvaluationExpanded"),
    assignmentHasTeacherEvaluation,
  };
};

const mapActionCreators = {
  updateSettings,
  openLinkInNewTab,
  setAIEvaluationExpandedState,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  withDerivedProps(getDerivedProps)
)(FieldAIEvaluation);
